/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import { action } from '@ember/object';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { MessageDisplay } from 'cio/app-models/in-app/in-app-message';
import { DisplayMode, ModalPosition, OverlayPosition } from 'cio/utils/in-app/message-config';

const MAX_MESSAGE_WIDTH = 10000;

interface DisplayArgs {
  display: MessageDisplay;
  onChange: (display: MessageDisplay) => void;
}

export default class Display extends Component<DisplayArgs> {
  @tracked modalPosition: ModalPosition = ModalPosition.Top;
  @tracked overlayPosition: OverlayPosition = OverlayPosition.TopCenter;
  @tracked elementId = '';
  @tracked isElementIdTouched = false;
  @tracked messageWidth = 0;
  @tracked overlayColor = '';
  @tracked overlayColorAlpha = 100;

  constructor(owner: unknown, args: DisplayArgs) {
    super(owner, args);
    let { display } = args;
    if (display?.value == null) {
      return;
    }
    if (display.mode === DisplayMode.Modal) {
      this.modalPosition = display.value as ModalPosition;
    } else if (display.mode === DisplayMode.Overlay) {
      this.overlayPosition = display.value as OverlayPosition;
    } else {
      this.elementId = display.value;
    }
    if (display.overlayColor != null) {
      this.setHexColorWithAlpha(display.overlayColor);
    }
    if (display.messageWidth != null) {
      this.messageWidth = display.messageWidth;
    }
  }

  get mode() {
    let { display } = this.args;
    return display.mode;
  }

  get isModal() {
    return this.mode === DisplayMode.Modal;
  }

  get isOverlay() {
    return this.mode === DisplayMode.Overlay;
  }

  get isInline() {
    return this.mode === DisplayMode.Inline;
  }

  @action
  setModal(position?: ModalPosition) {
    if (position != null) {
      this.modalPosition = position;
    }

    this.fireOnChange(DisplayMode.Modal, this.modalPosition, this.getHexColorWithAlpha(), this.messageWidth);
  }

  @action
  setDisplaySettings(overlayColor?: string, overlayColorAlpha?: number, messageWidth?: number) {
    if (overlayColor != null) {
      this.overlayColor = overlayColor;
    }

    if (overlayColorAlpha != null) {
      this.overlayColorAlpha = Number(overlayColorAlpha);
    }

    if (messageWidth != null) {
      if (messageWidth <= MAX_MESSAGE_WIDTH) {
        this.messageWidth = Number(messageWidth);
      } else {
        this.messageWidth = MAX_MESSAGE_WIDTH;
      }
    }

    if (this.isModal) {
      this.fireOnChange(DisplayMode.Modal, this.modalPosition, this.getHexColorWithAlpha(), this.messageWidth);
    } else if (this.isOverlay) {
      this.fireOnChange(DisplayMode.Overlay, this.overlayPosition, this.getHexColorWithAlpha(), this.messageWidth);
    }
  }

  private normalize = (val: number, max: number, min: number) => {
    return (val - min) / (max - min);
  };

  private getHexColorWithAlpha() {
    if (this.overlayColor !== '') {
      if (Number.isNaN(this.overlayColorAlpha)) {
        this.overlayColorAlpha = 100;
      }
      let alphaHex = `0${Math.round((255 / 100) * this.overlayColorAlpha).toString(16)}`.slice(-2).toUpperCase();
      return `${this.overlayColor}${alphaHex}`;
    } else {
      return '';
    }
  }

  private setHexColorWithAlpha(overlayColor: string) {
    let overlayColorOnly = overlayColor.slice(0, 7);
    let overlayColorHex = overlayColor.slice(7);
    let overlayColorAlpha = Math.round(this.normalize(parseInt(overlayColorHex, 16), 255, 0) * 100);

    this.overlayColor = overlayColorOnly;
    this.overlayColorAlpha = overlayColorAlpha;
  }

  @action
  selectModal() {
    this.setModal();
  }

  private fireOnChange(mode: DisplayMode, value: string, overlayColor: string, messageWidth: number) {
    this.args.onChange(new MessageDisplay(mode, value, overlayColor, messageWidth));
  }

  @action
  setOverlay(position?: OverlayPosition) {
    if (position != null) {
      this.overlayPosition = position;
    }
    this.fireOnChange(DisplayMode.Overlay, this.overlayPosition, this.getHexColorWithAlpha(), this.messageWidth);
  }

  @action
  selectOverlay() {
    this.setOverlay();
  }

  @action
  setInline(value?: string) {
    if (value != null) {
      this.elementId = value;
    }
    this.fireOnChange(DisplayMode.Inline, value ?? this.elementId, this.getHexColorWithAlpha(), this.messageWidth);
  }

  @action
  selectInline() {
    this.setInline();
  }

  @action
  markElementIdAsTouched() {
    if (!this.isElementIdTouched) {
      this.isElementIdTouched = true;
    }
  }
}
